import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';

/**
 * Above this quota size discrete pips stop reading as countable inventory,
 * so the runway falls back to a continuous meter with a numeric caption.
 * Up to 12 pips sit on one row in the sidebar; 13–24 wrap onto a second
 * row and still count at a glance. Beyond two rows they become texture,
 * not inventory.
 */
export const PIP_DISPLAY_LIMIT = 24;

export interface QuotaRunwayProps {
  readonly quota: number;
  readonly used: number;
  /** Accessible noun for the runway, e.g. "free extractions". */
  readonly noun?: string | undefined;
  readonly className?: string | undefined;
}

/**
 * Remaining-allowance display for small lifetime quotas.
 *
 * Renders what is LEFT as inventory (solid accent pips) rather than filling a
 * bar toward exhaustion, and never changes colour as the quota runs down:
 * urgency is the callers' job, at the moment of spending, not the meter's.
 */
export function QuotaRunway({ quota, used, noun = 'free extractions', className }: QuotaRunwayProps) {
  const spent = Math.min(Math.max(used, 0), quota);
  const remaining = quota - spent;
  const label = `${String(remaining)} of ${String(quota)} ${noun} left`;

  if (quota <= 0) {
    return null;
  }

  if (quota <= PIP_DISPLAY_LIMIT) {
    return (
      <div aria-label={label} className={cn('flex flex-wrap gap-1', className)} role="img">
        {[...Array(quota).keys()].map((position) => (
          <span
            className={cn(
              'h-1.5 w-3.5 rounded-full',
              position < spent ? 'bg-muted-foreground/25' : 'bg-accent',
            )}
            key={position}
          />
        ))}
      </div>
    );
  }

  // The meter keeps the inventory metaphor: it shows what REMAINS and drains
  // as the quota is spent, like a fuel gauge — it never fills toward zero.
  return (
    <Progress
      aria-label={label}
      className={cn('h-1.5 ring-1 ring-border', className)}
      value={Math.round((remaining / quota) * 100)}
    />
  );
}
